import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { calendarAPI, videosAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Calendar as CalendarIcon,
  Plus,
  Trash2,
  Clock,
  Film,
  Loader2,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from "date-fns";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function ContentCalendarPage() {
  const [calendarItems, setCalendarItems] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newItem, setNewItem] = useState({
    video_id: "",
    title: "",
    scheduled_date: format(new Date(), "yyyy-MM-dd"),
    scheduled_time: "12:00",
    notes: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [calendarRes, videosRes] = await Promise.all([
          calendarAPI.getItems(),
          videosAPI.getVideos("approved"),
        ]);
        setCalendarItems(calendarRes.data);
        setVideos(videosRes.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddItem = async () => {
    if (!newItem.title) {
      toast.error("Please enter a title");
      return;
    }

    setSaving(true);
    try {
      const response = await calendarAPI.createItem(newItem);
      setCalendarItems([...calendarItems, response.data]);
      setDialogOpen(false);
      setNewItem({
        video_id: "",
        title: "",
        scheduled_date: format(new Date(), "yyyy-MM-dd"),
        scheduled_time: "12:00",
        notes: "",
      });
      toast.success("Scheduled successfully!");
    } catch (error) {
      toast.error("Failed to schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await calendarAPI.deleteItem(itemId);
      setCalendarItems(calendarItems.filter((item) => item.id !== itemId));
      toast.success("Item removed");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const getDaysWithContent = () => {
    return calendarItems.map((item) => parseISO(item.scheduled_date));
  };

  const getItemsForDate = (date) => {
    return calendarItems.filter((item) =>
      isSameDay(parseISO(item.scheduled_date), date)
    );
  };

  const selectedDateItems = getItemsForDate(selectedDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="calendar-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <CalendarIcon className="w-8 h-8 text-[#00F0FF]" />
            Content Calendar
          </h1>
          <p className="text-muted-foreground mt-2">
            Plan and schedule your content for maximum reach.
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl gap-2 neon-glow" data-testid="schedule-content-btn">
              <Plus className="w-5 h-5" />
              Schedule Content
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border/50">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-white">
                Schedule Content
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white">Title</Label>
                <Input
                  placeholder="Content title..."
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  className="h-12 bg-secondary/50 border-border/50"
                  data-testid="schedule-title-input"
                />
              </div>

              {videos.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-white">Link to Video (Optional)</Label>
                  <Select
                    value={newItem.video_id || "none"}
                    onValueChange={(value) => setNewItem({ ...newItem, video_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="h-12 bg-secondary/50 border-border/50">
                      <SelectValue placeholder="Select a video..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {videos.map((video) => (
                        <SelectItem key={video.id} value={video.id}>
                          {video.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Date</Label>
                  <Input
                    type="date"
                    value={newItem.scheduled_date}
                    onChange={(e) => setNewItem({ ...newItem, scheduled_date: e.target.value })}
                    className="h-12 bg-secondary/50 border-border/50"
                    data-testid="schedule-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Time</Label>
                  <Input
                    type="time"
                    value={newItem.scheduled_time}
                    onChange={(e) => setNewItem({ ...newItem, scheduled_time: e.target.value })}
                    className="h-12 bg-secondary/50 border-border/50"
                    data-testid="schedule-time-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Notes</Label>
                <Textarea
                  placeholder="Add notes..."
                  value={newItem.notes}
                  onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                  className="bg-secondary/50 border-border/50"
                />
              </div>

              <Button
                onClick={handleAddItem}
                disabled={saving}
                className="w-full h-12 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl"
                data-testid="confirm-schedule-btn"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Schedule"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Calendar */}
        <motion.div variants={itemVariants} className="lg:col-span-5">
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                modifiers={{
                  hasContent: getDaysWithContent(),
                }}
                modifiersStyles={{
                  hasContent: {
                    backgroundColor: "rgba(0, 240, 255, 0.2)",
                    borderRadius: "50%",
                  },
                }}
                className="rounded-md"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Selected Day Content */}
        <motion.div variants={itemVariants} className="lg:col-span-7">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#00F0FF]" />
                {format(selectedDate, "EEEE, MMMM d, yyyy")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDateItems.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No content scheduled for this day</p>
                  <Button
                    onClick={() => {
                      setNewItem({ ...newItem, scheduled_date: format(selectedDate, "yyyy-MM-dd") });
                      setDialogOpen(true);
                    }}
                    variant="outline"
                    className="mt-4 border-[#00F0FF]/50 text-[#00F0FF]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Content
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedDateItems.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="p-4 bg-secondary/30 rounded-xl border border-border/30 flex items-start justify-between gap-4"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-[#00F0FF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Film className="w-5 h-5 text-[#00F0FF]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-white">{item.title}</h4>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="outline" className="badge-trending gap-1">
                              <Clock className="w-3 h-3" />
                              {item.scheduled_time}
                            </Badge>
                            <Badge className="badge-success">{item.status}</Badge>
                          </div>
                          {item.notes && (
                            <p className="text-sm text-muted-foreground mt-2">{item.notes}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Content */}
          {calendarItems.length > 0 && (
            <Card className="bg-card border-border/50 mt-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Upcoming Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {calendarItems
                    .filter((item) => new Date(item.scheduled_date) >= new Date())
                    .slice(0, 5)
                    .map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between py-2 border-b border-border/30 last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-[#00F0FF]" />
                          <span className="text-white text-sm">{item.title}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(parseISO(item.scheduled_date), "MMM d")} at {item.scheduled_time}
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
